// 강의 뷰어 관련 커스텀 훅들
import { useState, useContext, useRef } from 'react';
import { IdeMessengerContext } from '@edusense/features-ide-messenger';
import { isVSCodeEnvironment } from '@edusense/navigation-utils';
/**
 * VSCode 환경에서 강의 콘텐츠를 채팅에 붙여넣는 기능을 제공하는 훅
 */ export const useLectureChat = ()=>{
    const [isPasting, setIsPasting] = useState(false);
    const [selectedText, setSelectedText] = useState(null);
    const [selectionPosition, setSelectionPosition] = useState(null);
    const ideMessenger = useContext(IdeMessengerContext);
    const isVSCode = isVSCodeEnvironment();
    const markdownContainerRef = useRef(null);
    /**
   * 마우스 업 이벤트 처리 - 텍스트 선택 시 버튼 표시
   */ const handleMouseUp = ()=>{
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
            const selectedString = selection.toString();
            const container = markdownContainerRef.current;
            if (selectedString && container && selection.anchorNode && container.contains(selection.anchorNode)) {
                setSelectedText(selectedString);
                const range = selection.getRangeAt(0);
                const rect = range.getBoundingClientRect();
                const containerRect = container.getBoundingClientRect();
                const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
                let top = rect.bottom - containerRect.top + scrollTop + 5;
                let left = rect.right - containerRect.left + scrollLeft - 50;
                if (left < 0) left = 5;
                if (left + 100 > containerRect.width) left = containerRect.width - 100;
                if (top < 0) top = 5;
                setSelectionPosition({
                    top,
                    left
                });
            } else {
                setSelectedText(null);
                setSelectionPosition(null);
            }
        } else {
            setSelectedText(null);
            setSelectionPosition(null);
        }
    };
    /**
   * 선택된 마크다운 텍스트를 채팅에 붙여넣기
   */ const handlePasteSelectedMarkdownToChat = (selectedMarkdown)=>{
        if (!ideMessenger || !selectedMarkdown) {
            console.warn("IDE Messenger context is not available or no text selected.");
            return;
        }
        console.log("Requesting add SELECTED markdown to Chat:", selectedMarkdown);
        const editorContent = {
            type: "doc",
            content: [
                {
                    type: "educationBlock",
                    attrs: {
                        title: `Selected Text`,
                        content: selectedMarkdown,
                        category: "Selected Text",
                        markdown: selectedMarkdown
                    }
                }
            ]
        };
        ideMessenger?.post('addEducationContextToChat', {
            content: editorContent,
            shouldRun: false,
            prompt: ""
        });
        setSelectedText(null);
        setSelectionPosition(null);
        window.getSelection()?.removeAllRanges();
    };
    /**
   * 전체 강의 내용을 채팅에 붙여넣기
   */ const handlePasteToChat = (lecture)=>{
        if (!ideMessenger || !lecture) {
            console.warn("IDE Messenger context is not available or lecture data is missing.");
            return;
        }
        setIsPasting(true);
        const title = lecture.title ?? '제목 없음';
        const description = lecture.description ?? '';
        const markdown = lecture.markdownContent ?? '';
        let contentBody = `**${title}**\n\n`;
        if (description) {
            contentBody += `**요약:**\n${description}\n\n`;
        }
        if (markdown) {
            contentBody += `**콘텐츠:**\n\`\`\`markdown\n${markdown}\n\`\`\``;
        } else {
            contentBody += "_마크다운 콘텐츠 없음_";
        }
        const editorContent = {
            type: "doc",
            content: [
                {
                    type: "educationBlock",
                    attrs: {
                        title: `강의: ${title}`,
                        content: contentBody,
                        category: "Lecture Content",
                        markdown: contentBody
                    }
                }
            ]
        };
        console.log("Requesting add lecture content to Chat:", editorContent);
        ideMessenger?.post('addEducationContextToChat', {
            content: editorContent,
            shouldRun: false,
            prompt: ""
        });
        setTimeout(()=>setIsPasting(false), 1000);
    };
    return {
        isPasting,
        selectedText,
        selectionPosition,
        isVSCode,
        markdownContainerRef,
        handleMouseUp,
        handlePasteSelectedMarkdownToChat,
        handlePasteToChat
    };
};
